import { API } from '@/config/api';
import React, { useState, useEffect } from 'react';
import { useOutletContext } from 'react-router-dom';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Slider } from '@/components/ui/slider';
import { 
  Brain, Focus, Moon, TrendingUp, Calendar, 
  CheckCircle, AlertCircle, BarChart3, ArrowUp, ArrowDown, Minus
} from 'lucide-react';
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, 
  ResponsiveContainer, Legend, AreaChart, Area
} from 'recharts';
import { toast } from 'sonner';


export default function NeuroOptimization() {
  const { user } = useOutletContext();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [todayMetrics, setTodayMetrics] = useState(null);
  const [history, setHistory] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [activeTab, setActiveTab] = useState('track');
  
  // Form state
  const [focusScore, setFocusScore] = useState(5);
  const [mentalClarity, setMentalClarity] = useState(5);
  const [sleepLatency, setSleepLatency] = useState(15);
  const [notes, setNotes] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [todayRes, historyRes, analyticsRes] = await Promise.all([
        fetch(`${API}/tracking/neuro/today`, { credentials: 'include' }),
        fetch(`${API}/tracking/neuro/history?days=30`, { credentials: 'include' }),
        fetch(`${API}/tracking/neuro/analytics`, { credentials: 'include' })
      ]);

      if (todayRes.ok) {
        const today = await todayRes.json();
        setTodayMetrics(today);
        if (today.focus_score) setFocusScore(today.focus_score);
        if (today.mental_clarity) setMentalClarity(today.mental_clarity);
        if (today.sleep_latency_mins) setSleepLatency(today.sleep_latency_mins);
        if (today.notes) setNotes(today.notes);
      }

      if (historyRes.ok) {
        setHistory(await historyRes.json());
      }

      if (analyticsRes.ok) {
        setAnalytics(await analyticsRes.json());
      }
    } catch (error) {
      console.error('Failed to fetch neuro data:', error);
      toast.error('Failed to load neuro metrics');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const today = new Date().toISOString().split('T')[0];
      const response = await fetch(`${API}/tracking/neuro`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({
          date: today,
          focus_score: focusScore,
          mental_clarity: mentalClarity,
          sleep_latency_mins: sleepLatency,
          notes: notes
        })
      });

      if (!response.ok) throw new Error('Failed to save metrics');
      
      toast.success('Neuro metrics saved successfully!');
      fetchData();
    } catch (error) {
      toast.error('Failed to save metrics');
    } finally {
      setSaving(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 8) return 'text-green-500';
    if (score >= 6) return 'text-yellow-500';
    if (score >= 4) return 'text-orange-500';
    return 'text-red-500';
  };

  const getScoreLabel = (score) => {
    if (score >= 9) return 'Excellent';
    if (score >= 7) return 'Good';
    if (score >= 5) return 'Average';
    if (score >= 3) return 'Below Average';
    return 'Poor';
  };

  const getSleepLatencyLabel = (mins) => {
    if (mins <= 10) return { label: 'Excellent', color: 'text-green-500' };
    if (mins <= 20) return { label: 'Good', color: 'text-green-400' };
    if (mins <= 30) return { label: 'Average', color: 'text-yellow-500' };
    if (mins <= 45) return { label: 'Slow', color: 'text-orange-500' };
    return { label: 'Poor', color: 'text-red-500' };
  };

  const getTrendIcon = (rate) => {
    if (rate > 0) return <ArrowUp className="h-4 w-4 text-green-500" />;
    if (rate < 0) return <ArrowDown className="h-4 w-4 text-red-500" />;
    return <Minus className="h-4 w-4 text-gray-500" />;
  };

  // Prepare chart data
  const chartData = [...history].reverse().map(h => ({
    date: new Date(h.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }),
    focus: h.focus_score,
    clarity: h.mental_clarity,
    sleep: h.sleep_latency_mins ? Math.max(10 - (h.sleep_latency_mins / 10), 0) : null // Convert to 0-10 scale (inverse)
  }));

  if (loading) {
    return (
      <div className="p-4 md:p-8 max-w-7xl mx-auto">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin h-12 w-12 border-4 border-primary border-t-transparent rounded-full"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-4 md:p-8 max-w-7xl mx-auto" data-testid="neuro-optimization-page">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl md:text-4xl font-bold tracking-tight mb-2 flex items-center gap-3">
          <Brain className="h-10 w-10 text-primary" />
          Neuro-Optimization Dashboard
        </h1>
        <p className="text-muted-foreground">Track your cognitive performance and sleep metrics</p>
      </div>

      {/* Tabs */}
      <div className="flex gap-2 mb-6 overflow-x-auto pb-2">
        <Button
          variant={activeTab === 'track' ? 'default' : 'outline'}
          onClick={() => setActiveTab('track')}
          data-testid="track-tab"
        >
          <CheckCircle className="h-4 w-4 mr-2" />
          Track Today
        </Button>
        <Button
          variant={activeTab === 'analytics' ? 'default' : 'outline'}
          onClick={() => setActiveTab('analytics')}
          data-testid="analytics-tab"
        >
          <BarChart3 className="h-4 w-4 mr-2" />
          Analytics
        </Button>
        <Button
          variant={activeTab === 'history' ? 'default' : 'outline'}
          onClick={() => setActiveTab('history')}
          data-testid="history-tab"
        >
          <Calendar className="h-4 w-4 mr-2" />
          History
        </Button>
      </div>

      {/* Track Tab */}
      {activeTab === 'track' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Today's Metrics Input */}
          <Card className="p-6 glass-card">
            <h2 className="text-xl font-bold mb-6">Log Today's Metrics</h2>
            
            {/* Focus Score */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-3">
                <Label className="flex items-center gap-2 text-lg">
                  <Focus className="h-5 w-5 text-blue-500" />
                  Focus Score
                </Label>
                <span className={`text-2xl font-bold ${getScoreColor(focusScore)}`}>
                  {focusScore}/10
                </span>
              </div>
              <Slider
                value={[focusScore]}
                onValueChange={(v) => setFocusScore(v[0])}
                max={10}
                min={1}
                step={1}
                className="mb-2"
                data-testid="focus-score-slider"
              />
              <p className="text-sm text-muted-foreground">
                Rate your ability to concentrate and stay on task today: <span className={getScoreColor(focusScore)}>{getScoreLabel(focusScore)}</span>
              </p>
            </div>

            {/* Mental Clarity */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-3">
                <Label className="flex items-center gap-2 text-lg">
                  <Brain className="h-5 w-5 text-purple-500" />
                  Mental Clarity
                </Label>
                <span className={`text-2xl font-bold ${getScoreColor(mentalClarity)}`}>
                  {mentalClarity}/10
                </span>
              </div>
              <Slider
                value={[mentalClarity]}
                onValueChange={(v) => setMentalClarity(v[0])}
                max={10}
                min={1}
                step={1}
                className="mb-2"
                data-testid="mental-clarity-slider"
              />
              <p className="text-sm text-muted-foreground">
                Rate your mental sharpness and clarity of thought: <span className={getScoreColor(mentalClarity)}>{getScoreLabel(mentalClarity)}</span>
              </p>
            </div>

            {/* Sleep Latency */}
            <div className="mb-8">
              <div className="flex items-center justify-between mb-3">
                <Label className="flex items-center gap-2 text-lg">
                  <Moon className="h-5 w-5 text-indigo-500" />
                  Sleep Latency
                </Label>
                <span className={`text-2xl font-bold ${getSleepLatencyLabel(sleepLatency).color}`}>
                  {sleepLatency} min
                </span>
              </div>
              <Slider
                value={[sleepLatency]}
                onValueChange={(v) => setSleepLatency(v[0])}
                max={90}
                min={1}
                step={1}
                className="mb-2"
                data-testid="sleep-latency-slider"
              />
              <p className="text-sm text-muted-foreground">
                Time to fall asleep last night: <span className={getSleepLatencyLabel(sleepLatency).color}>{getSleepLatencyLabel(sleepLatency).label}</span>
              </p>
            </div>

            {/* Notes */}
            <div className="mb-6">
              <Label className="mb-2 block">Notes (Optional)</Label>
              <Textarea
                value={notes}
                onChange={(e) => setNotes(e.target.value)}
                placeholder="Any observations about your cognitive state, sleep quality, or factors that may have influenced today's metrics..."
                rows={3}
                data-testid="neuro-notes"
              />
            </div>

            <Button 
              onClick={handleSave} 
              className="w-full" 
              disabled={saving}
              data-testid="save-neuro-metrics"
            >
              {saving ? 'Saving...' : 'Save Today\'s Metrics'}
            </Button>
          </Card>

          {/* Quick Stats */}
          <div className="space-y-6">
            {/* Today's Summary */}
            <Card className="p-6 glass-card">
              <h2 className="text-xl font-bold mb-4">Today's Summary</h2>
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-4 rounded-lg bg-blue-500/10">
                  <Focus className="h-8 w-8 mx-auto mb-2 text-blue-500" />
                  <p className="text-2xl font-bold">{focusScore}</p>
                  <p className="text-xs text-muted-foreground">Focus</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-purple-500/10">
                  <Brain className="h-8 w-8 mx-auto mb-2 text-purple-500" />
                  <p className="text-2xl font-bold">{mentalClarity}</p>
                  <p className="text-xs text-muted-foreground">Clarity</p>
                </div>
                <div className="text-center p-4 rounded-lg bg-indigo-500/10">
                  <Moon className="h-8 w-8 mx-auto mb-2 text-indigo-500" />
                  <p className="text-2xl font-bold">{sleepLatency}m</p>
                  <p className="text-xs text-muted-foreground">Sleep Onset</p>
                </div>
              </div>
            </Card>

            {/* Analytics Summary */}
            {analytics && analytics.total_entries > 0 && (
              <Card className="p-6 glass-card">
                <h2 className="text-xl font-bold mb-4 flex items-center gap-2">
                  <TrendingUp className="h-5 w-5 text-primary" />
                  Performance Overview
                </h2>
                <div className="space-y-4">
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Avg Focus Score</span>
                    <span className={`font-bold ${getScoreColor(analytics.focus_score_avg || 0)}`}>
                      {analytics.focus_score_avg?.toFixed(1) || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Avg Mental Clarity</span>
                    <span className={`font-bold ${getScoreColor(analytics.mental_clarity_avg || 0)}`}>
                      {analytics.mental_clarity_avg?.toFixed(1) || 'N/A'}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Avg Sleep Latency</span>
                    <span className={`font-bold ${getSleepLatencyLabel(analytics.sleep_latency_avg || 30).color}`}>
                      {analytics.sleep_latency_avg?.toFixed(0) || 'N/A'} min
                    </span>
                  </div>
                  {analytics.improvement_rate !== null && (
                    <div className="flex justify-between items-center pt-2 border-t">
                      <span className="text-muted-foreground">Focus Trend</span>
                      <span className="font-bold flex items-center gap-1">
                        {getTrendIcon(analytics.improvement_rate)}
                        {analytics.improvement_rate > 0 ? '+' : ''}{analytics.improvement_rate}%
                      </span>
                    </div>
                  )}
                  <div className="flex justify-between items-center">
                    <span className="text-muted-foreground">Total Entries</span>
                    <span className="font-bold">{analytics.total_entries}</span>
                  </div>
                </div>
              </Card>
            )}

            {/* Cognitive Tips */}
            <Card className="p-6 glass-card">
              <h2 className="text-xl font-bold mb-4">Optimization Tips</h2>
              <div className="space-y-3 text-sm">
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-blue-500 mt-0.5 flex-shrink-0" />
                  <p>Nootropic peptides like Semax, Selank, and Dihexa may support cognitive function.</p>
                </div>
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-purple-500 mt-0.5 flex-shrink-0" />
                  <p>Track metrics consistently for accurate trend analysis.</p>
                </div>
                <div className="flex items-start gap-2">
                  <AlertCircle className="h-4 w-4 text-indigo-500 mt-0.5 flex-shrink-0" />
                  <p>Sleep latency under 20 minutes indicates optimal sleep onset.</p>
                </div>
              </div>
            </Card>
          </div>
        </div>
      )}

      {/* Analytics Tab */}
      {activeTab === 'analytics' && (
        <div className="space-y-6">
          {/* Trend Chart */}
          {chartData.length > 1 && (
            <Card className="p-6 glass-card">
              <h2 className="text-xl font-bold mb-4">Performance Trends (Last 30 Days)</h2>
              <div className="h-[300px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={chartData}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="date" stroke="#888" />
                    <YAxis domain={[0, 10]} stroke="#888" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1a1a2e', 
                        border: '1px solid #333',
                        borderRadius: '8px'
                      }} 
                    />
                    <Legend />
                    <Line 
                      type="monotone" 
                      dataKey="focus" 
                      stroke="#3b82f6" 
                      strokeWidth={2}
                      dot={{ fill: '#3b82f6' }}
                      name="Focus Score"
                    />
                    <Line 
                      type="monotone" 
                      dataKey="clarity" 
                      stroke="#a855f7" 
                      strokeWidth={2}
                      dot={{ fill: '#a855f7' }}
                      name="Mental Clarity"
                    />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            </Card>
          )}

          {/* Weekly Trends */}
          {analytics?.weekly_trends?.length > 0 && (
            <Card className="p-6 glass-card">
              <h2 className="text-xl font-bold mb-4">Weekly Averages</h2>
              <div className="h-[250px]">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={analytics.weekly_trends}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#333" />
                    <XAxis dataKey="week" stroke="#888" />
                    <YAxis domain={[0, 10]} stroke="#888" />
                    <Tooltip 
                      contentStyle={{ 
                        backgroundColor: '#1a1a2e', 
                        border: '1px solid #333',
                        borderRadius: '8px'
                      }} 
                    />
                    <Area 
                      type="monotone" 
                      dataKey="focus_avg" 
                      stroke="#3b82f6" 
                      fill="#3b82f6"
                      fillOpacity={0.3}
                      name="Avg Focus"
                    />
                    <Area 
                      type="monotone" 
                      dataKey="clarity_avg" 
                      stroke="#a855f7" 
                      fill="#a855f7"
                      fillOpacity={0.3}
                      name="Avg Clarity"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </Card>
          )}

          {/* Stats Grid */}
          {analytics && (
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <Card className="p-4 glass-card text-center">
                <p className="text-3xl font-bold text-blue-500">{analytics.focus_score_avg?.toFixed(1) || '-'}</p>
                <p className="text-sm text-muted-foreground">Avg Focus</p>
              </Card>
              <Card className="p-4 glass-card text-center">
                <p className="text-3xl font-bold text-purple-500">{analytics.mental_clarity_avg?.toFixed(1) || '-'}</p>
                <p className="text-sm text-muted-foreground">Avg Clarity</p>
              </Card>
              <Card className="p-4 glass-card text-center">
                <p className="text-3xl font-bold text-indigo-500">{analytics.sleep_latency_avg?.toFixed(0) || '-'}</p>
                <p className="text-sm text-muted-foreground">Avg Sleep (min)</p>
              </Card>
              <Card className="p-4 glass-card text-center">
                <p className="text-3xl font-bold text-primary">{analytics.total_entries || 0}</p>
                <p className="text-sm text-muted-foreground">Total Entries</p>
              </Card>
            </div>
          )}

          {(!analytics || analytics.total_entries === 0) && (
            <Card className="p-8 glass-card text-center">
              <Brain className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-xl font-bold mb-2">No Data Yet</h3>
              <p className="text-muted-foreground mb-4">Start tracking your cognitive metrics to see analytics.</p>
              <Button onClick={() => setActiveTab('track')}>Start Tracking</Button>
            </Card>
          )}
        </div>
      )}

      {/* History Tab */}
      {activeTab === 'history' && (
        <div className="space-y-4">
          {history.length === 0 ? (
            <Card className="p-8 glass-card text-center">
              <Calendar className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
              <h3 className="text-xl font-bold mb-2">No History</h3>
              <p className="text-muted-foreground">Your tracking history will appear here.</p>
            </Card>
          ) : (
            history.map((entry) => (
              <Card key={entry.metric_id || entry.date} className="p-4 glass-card">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="text-center">
                      <p className="text-xs text-muted-foreground">
                        {new Date(entry.date).toLocaleDateString('en-US', { weekday: 'short' })}
                      </p>
                      <p className="font-bold">
                        {new Date(entry.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      </p>
                    </div>
                    <div className="h-12 w-px bg-border" />
                    <div className="flex gap-6">
                      {entry.focus_score && (
                        <div className="flex items-center gap-2">
                          <Focus className="h-4 w-4 text-blue-500" />
                          <span className={`font-bold ${getScoreColor(entry.focus_score)}`}>{entry.focus_score}</span>
                        </div>
                      )}
                      {entry.mental_clarity && (
                        <div className="flex items-center gap-2">
                          <Brain className="h-4 w-4 text-purple-500" />
                          <span className={`font-bold ${getScoreColor(entry.mental_clarity)}`}>{entry.mental_clarity}</span>
                        </div>
                      )}
                      {entry.sleep_latency_mins && (
                        <div className="flex items-center gap-2">
                          <Moon className="h-4 w-4 text-indigo-500" />
                          <span className={`font-bold ${getSleepLatencyLabel(entry.sleep_latency_mins).color}`}>
                            {entry.sleep_latency_mins}m
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                {entry.notes && (
                  <p className="mt-3 text-sm text-muted-foreground border-t pt-3">{entry.notes}</p>
                )}
              </Card>
            ))
          )}
        </div>
      )}
    </div>
  );
}
